/**
 * Level 2 condition builder: a flat AND/OR over a list of typed comparisons.
 * No nesting, no parentheses, no boolean operators between sub-conditions.
 * If authors need deeper expressiveness, the escape hatch is chained
 * conditional destinations / variants / feedback variants — each chain
 * entry has its own ConditionBuilder.
 *
 * Used in four places: `choice.visibleIf`, scene variant `condition`,
 * destination chain entry `condition`, and feedback variant `condition`
 * (the last lands with FeedbackVariantsEditor in Phase 1b.2c).
 *
 * Contract:
 *   - `condition === null` or `condition === undefined`: empty state — the
 *     builder shows a hint and an "Add comparison" affordance.
 *   - `onChange(null)`: called when the user clears the LAST comparison, so
 *     the parent can do the "remove on clear" gesture. Chain editors interpret
 *     this as "remove this entry from the chain"; `visibleIf` parents
 *     interpret it as "unset visibleIf — always visible."
 *   - `onChange(condition)`: called with a valid Condition otherwise.
 *
 * The empty state is the place where the prototype's "Always X / Only X if…"
 * pattern lives. The exact wording is a prop because it differs per context
 * (visibility says "Always visible / Only show if…"; a chain entry says
 * something else entirely, but the chain editor handles the empty state
 * itself there).
 */
import { Plus, X } from 'lucide-react';
import type {
  Combinator,
  Comparison,
  Condition,
  VariableDecl,
} from '@branchworks/shared';
import { ComparisonRow } from './ComparisonRow';
import { useScenarioStore } from '../../store/scenarioStore';

interface ConditionBuilderProps {
  condition: Condition | null | undefined;
  variables: Record<string, VariableDecl>;
  onChange: (next: Condition | null) => void;
  /** Empty-state wording: "Always visible" / "Always shown" / etc. */
  emptyLabel?: string;
  /** Verb for the empty-state CTA: "Only show if…" / "Use this if…" / etc. */
  addLabel?: string;
}

export function ConditionBuilder({
  condition,
  variables,
  onChange,
  emptyLabel = 'Always',
  addLabel = 'Add a condition',
}: ConditionBuilderProps) {
  const variableNames = Object.keys(variables || {});
  const hasVariables = variableNames.length > 0;
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const comparisons = condition?.comparisons || [];
  const combinator: Combinator = condition?.combinator || 'all';

  // --- Empty state ---
  if (!condition || comparisons.length === 0) {
    return (
      <div className="text-xs text-ink-2 italic">
        {emptyLabel}.
        {hasVariables ? (
          <>
            {' '}
            <button
              onClick={() => {
                const firstVar = variableNames[0];
                const decl = variables[firstVar];
                onChange({
                  combinator: 'all',
                  comparisons: [{ variable: firstVar, op: '==', value: decl.default }],
                });
              }}
              className="not-italic text-ink hover:underline"
            >
              {addLabel}
            </button>
          </>
        ) : (
          <span className="not-italic">
            {' '}
            (
            <button
              onClick={() => setOpenModal('variables')}
              className="underline text-ink-2 hover:text-ink"
            >
              declare a variable
            </button>
            {' '}to add a condition)
          </span>
        )}
      </div>
    );
  }

  // --- Active condition ---
  const updateComparison = (idx: number, patch: Partial<Comparison>) => {
    const next = comparisons.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange({ combinator, comparisons: next });
  };

  const removeComparison = (idx: number) => {
    const next = comparisons.filter((_, i) => i !== idx);
    if (next.length === 0) {
      // The "remove on clear" gesture: parent decides what to do.
      onChange(null);
    } else {
      onChange({ combinator, comparisons: next });
    }
  };

  const addComparison = () => {
    const firstVar = variableNames[0];
    const decl = variables[firstVar];
    onChange({
      combinator,
      comparisons: [
        ...comparisons,
        { variable: firstVar, op: '==', value: decl.default },
      ],
    });
  };

  const setCombinator = (next: Combinator) => onChange({ combinator: next, comparisons });

  return (
    <div className="space-y-1.5">
      {comparisons.map((cmp, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {/* Combinator dropdown only shows from row 2 onwards. */}
          {i === 0 ? (
            <span className="text-[10px] uppercase tracking-widest text-ink-2 w-10 flex-shrink-0">
              If
            </span>
          ) : (
            <select
              value={combinator}
              onChange={(e) => setCombinator(e.target.value as Combinator)}
              className="text-[10px] uppercase tracking-widest w-10 flex-shrink-0 bg-transparent text-ink-2 border-0 outline-none p-0 m-0"
            >
              <option value="all">AND</option>
              <option value="any">OR</option>
            </select>
          )}
          <div className="flex-1 min-w-0">
            <ComparisonRow
              comparison={cmp}
              variables={variables}
              onChange={(patch) => updateComparison(i, patch)}
            />
          </div>
          <button
            onClick={() => removeComparison(i)}
            className="p-1 rounded hover:bg-cream flex-shrink-0 text-terracotta-dark"
            title="Remove comparison"
            aria-label="Remove comparison"
          >
            <X size={12} />
          </button>
        </div>
      ))}
      <button
        onClick={addComparison}
        className="text-xs flex items-center gap-1 hover:underline text-ink-2"
      >
        <Plus size={11} /> Add comparison
      </button>
    </div>
  );
}
